import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Plus, Edit, Eye, Trash2, MapPin, Lightbulb, AlertTriangle } from 'lucide-react';
import { usePostesIluminacao } from '../context/PostesIluminacaoProvider';
import { formatarData, obterCorStatus, obterIconeStatus } from '../utils/formatters';
import { STATUS_POSTE, TIPOS_LAMPADA } from '../utils/constants';

export default function ListaPostes() {
  const {
    postes,
    localidades,
    loading,
    filtros,
    setFiltros,
    excluirPoste
  } = usePostesIluminacao();

  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [posteParaExcluir, setPosteParaExcluir] = useState(null);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      status: '',
      localidade: '',
      tipoLampada: '',
      busca: ''
    });
  };

  const confirmarExclusao = async () => {
    if (posteParaExcluir) {
      await excluirPoste(posteParaExcluir.id);
      setPosteParaExcluir(null);
    }
  };

  const obterStatusClass = (status) => {
    const classes = {
      'Funcionando': 'status-funcionando',
      'Alerta': 'status-alerta',
      'Defeito': 'status-defeito'
    };
    return classes[status] || '';
  };

  return (
    <div className="postes-iluminacao">
      <div className="lista-full-container">
        <div className="lista-header">
          <div className="header-content">
            <h1>
              <Lightbulb size={24} color="#ff6f00" />
              Postes de Iluminação Pública
            </h1>
            <p>Gerencie os postes de iluminação do município</p>
          </div>
          <Link to="/postes-iluminacao/cadastro" className="button primary">
            <Plus size={16} />
            Novo Poste
          </Link>
        </div>

        <div className="lista-content-wrapper">

          {/* Barra de Busca e Filtros */}
          <div className="search-filter-bar">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Buscar por número de identificação..."
                value={filtros.busca}
                onChange={(e) => handleFiltroChange('busca', e.target.value)}
              />
            </div>
            <button
              className="filter-toggle"
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
            >
              <Filter size={16} />
              Filtros
            </button>
          </div>

          {/* Painel de Filtros */}
          {mostrarFiltros && (
            <div className="filters-panel">
              <div className="filters-grid">
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    value={filtros.status}
                    onChange={(e) => handleFiltroChange('status', e.target.value)}
                  >
                    <option value="">Todos os status</option>
                    {Object.values(STATUS_POSTE).map(status => (
                      <option key={status} value={status}>
                        {obterIconeStatus(status)} {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Bairro</label>
                  <select
                    value={filtros.localidade}
                    onChange={(e) => handleFiltroChange('localidade', e.target.value)}
                  >
                    <option value="">Todos os bairros</option>
                    {localidades.map(localidade => (
                      <option key={localidade.id} value={localidade.id}>
                        {localidade.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Tipo de Lâmpada</label>
                  <select
                    value={filtros.tipoLampada}
                    onChange={(e) => handleFiltroChange('tipoLampada', e.target.value)}
                  >
                    <option value="">Todos os tipos</option>
                    {TIPOS_LAMPADA.map(tipo => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-actions">
                  <button onClick={limparFiltros} className="button secondary">
                    Limpar Filtros
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Estatísticas Rápidas */}
          <div className="quick-stats">
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{postes.length}</span>
            </div>
            <div className="stat-item funcionando">
              <span className="stat-label">Funcionando:</span>
              <span className="stat-value">
                {postes.filter(p => p.status === 'Funcionando').length}
              </span>
            </div>
            <div className="stat-item alerta">
              <span className="stat-label">Alerta:</span>
              <span className="stat-value">
                {postes.filter(p => p.status === 'Alerta').length}
              </span>
            </div>
            <div className="stat-item defeito">
              <span className="stat-label">Defeito:</span>
              <span className="stat-value">
                {postes.filter(p => p.status === 'Defeito').length}
              </span>
            </div>
          </div>

          {/* Lista de Postes */}
          <div className="lista-content">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Carregando postes...</p>
              </div>
            ) : postes.length === 0 ? (
              <div className="empty-state">
                <Lightbulb size={48} />
                <h3>Nenhum poste encontrado</h3>
                <p>
                  {filtros.busca || filtros.status || filtros.localidade || filtros.tipoLampada
                    ? 'Tente ajustar os filtros de busca'
                    : 'Comece cadastrando o primeiro poste de iluminação'
                  }
                </p>
                {!filtros.busca && !filtros.status && !filtros.localidade && !filtros.tipoLampada && (
                  <Link to="/postes-iluminacao/cadastro" className="button primary">
                    <Plus size={16} />
                    Cadastrar Primeiro Poste
                  </Link>
                )}
              </div>
            ) : (
              <div className="postes-list-vertical">
                {postes.map(poste => (
                  <div key={poste.id} className="poste-card-horizontal">
                    <div className="card-main-content">
                      {/* Cabeçalho do card */}
                      <div className="card-header-section">
                        <h3 className="poste-title">#{poste.numero_identificacao}</h3>
                        <span className={`status-badge ${obterStatusClass(poste.status)}`}>
                          {poste.status}
                        </span>
                      </div>

                      {/* Corpo do card */}
                      <div className="card-body-section">
                        <div className="info-line">
                          <span className="info-label">Bairro:</span>
                          <span className="info-value">{poste.ccbairros?.descricao || 'Não informado'}</span>
                        </div>

                        <div className="info-line">
                          <span className="info-label">Tipo de Lâmpada:</span>
                          <span className="info-value">
                            {poste.tipo_lampada}
                            {poste.potencia && <span className="potencia"> ({poste.potencia})</span>}
                          </span>
                        </div>

                        {poste.altura_poste && (
                          <div className="info-line">
                            <span className="info-label">Altura:</span>
                            <span className="info-value">{poste.altura_poste}</span>
                          </div>
                        )}

                        {poste.data_instalacao && (
                          <div className="info-line">
                            <span className="info-label">Data de Instalação:</span>
                            <span className="info-value">{formatarData(poste.data_instalacao)}</span>
                          </div>
                        )}

                        <div className="info-line">
                          <span className="info-label">Coordenadas:</span>
                          <span className="info-value coordinates">
                            {poste.latitude?.toFixed(6)}, {poste.longitude?.toFixed(6)}
                          </span>
                        </div>

                        {poste.observacoes && (
                          <div className="info-line">
                            <span className="info-label">Observações:</span>
                            <span className="info-value">{poste.observacoes}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Botões na lateral direita */}
                    <div className="card-actions-vertical">
                      <Link
                        to={`/postes-iluminacao/detalhes/${poste.id}`}
                        className="action-button-full view"
                      >
                        <Eye size={16} />
                        Ver Detalhes
                      </Link>
                      <Link
                        to={`/postes-iluminacao/editar/${poste.id}`}
                        className="action-button-full edit"
                      >
                        <Edit size={16} />
                        Editar
                      </Link>
                      <button
                        onClick={() => setPosteParaExcluir(poste)}
                        className="action-button-full delete"
                      >
                        <Trash2 size={16} />
                        Excluir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal de Confirmação de Exclusão */}
          {posteParaExcluir && (
            <div className="modal-overlay">
              <div className="modal">
                <div className="modal-header">
                  <h3>Confirmar Exclusão</h3>
                </div>
                <div className="modal-content">
                  <p>
                    Tem certeza que deseja excluir o poste <strong>#{posteParaExcluir.numero_identificacao}</strong>?
                  </p>
                  <p className="warning-text">
                    <AlertTriangle size={16} />
                    Esta ação não pode ser desfeita.
                  </p>
                </div>
                <div className="modal-actions">
                  <button
                    onClick={() => setPosteParaExcluir(null)}
                    className="button secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmarExclusao}
                    className="button danger"
                  >
                    Excluir Poste
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}